<!-- src/components/common/FolderExplorer.vue -->
<template>
  <div class="folder-explorer-container">
    <!-- Explorer Header & Breadcrumbs -->
    <div class="explorer-header">
      <div class="d-flex align-center flex-wrap ga-2 flex-grow-1">
        <!-- Back / Up Button -->
        <v-btn
          icon="mdi-arrow-up-bold"
          variant="tonal"
          density="comfortable"
          size="small"
          color="primary"
          :disabled="!currentPath"
          title="ขึ้นหนึ่งระดับ"
          @click="navigateUp"
        />

        <!-- Breadcrumbs Navigation -->
        <div class="breadcrumbs-bar">
          <v-chip
            size="small"
            variant="text"
            class="breadcrumb-item cursor-pointer font-weight-bold"
            prepend-icon="mdi-server"
            @click="navigateTo('')"
          >
            SKO Files
          </v-chip>

          <template v-for="(crumb, idx) in pathCrumbs" :key="idx">
            <v-icon icon="mdi-chevron-right" size="14" class="text-medium-emphasis mx-1" />
            <v-chip
              size="small"
              variant="text"
              class="breadcrumb-item cursor-pointer"
              :class="{ 'text-primary font-weight-bold': idx === pathCrumbs.length - 1 }"
              @click="navigateTo(crumb.path)"
            >
              {{ crumb.name }}
            </v-chip>
          </template>
        </div>
      </div>

      <!-- Search / Filter Field -->
      <div class="search-box">
        <v-text-field
          v-model="searchQuery"
          density="compact"
          variant="outlined"
          placeholder="ค้นหาไฟล์หรือโฟลเดอร์..."
          prepend-inner-icon="mdi-magnify"
          clearable
          hide-details
          class="search-input"
        />
      </div>

      <!-- Reload Button -->
      <v-btn
        icon="mdi-refresh"
        variant="tonal"
        density="comfortable"
        size="small"
        :loading="loading"
        title="รีเฟรชรายการ"
        @click="loadDirectory(currentPath)"
      />
    </div>

    <!-- Main Content Area -->
    <div class="explorer-body">
      <!-- Loading State -->
      <div v-if="loading" class="d-flex flex-column align-center justify-center h-100 py-12">
        <v-progress-circular indeterminate color="primary" size="48" class="mb-3" />
        <span class="text-caption text-medium-emphasis">กำลังอ่านรายการไฟล์...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="d-flex flex-column align-center justify-center h-100 py-12 text-center pa-6">
        <v-icon icon="mdi-alert-circle-outline" color="warning" size="48" class="mb-3" />
        <div class="text-subtitle-1 text-slate-200">ไม่สามารถโหลดรายการไฟล์ได้</div>
        <div class="text-caption text-medium-emphasis mt-1 mb-4">{{ error }}</div>
        <v-btn color="primary" prepend-icon="mdi-refresh" size="small" @click="loadDirectory(currentPath)">
          ลองใหม่อีกครั้ง
        </v-btn>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredItems.length === 0" class="d-flex flex-column align-center justify-center h-100 py-12 text-center">
        <v-icon icon="mdi-folder-open-outline" size="48" class="text-disabled mb-2" />
        <div class="text-body-2 text-medium-emphasis">ไม่พบรายการไฟล์ในโฟลเดอร์นี้</div>
      </div>

      <!-- Directory Table / List Directive -->
      <div v-else class="directory-list-view">
        <table class="directory-table">
          <thead>
            <tr>
              <th class="col-name text-left">ชื่อไฟล์ / โฟลเดอร์</th>
              <th class="col-size text-right">ขนาด</th>
              <th class="col-mtime text-right d-none d-sm-table-cell">วันที่แก้ไข</th>
              <th class="col-action text-right">เปิด</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in filteredItems"
              :key="item.name"
              class="item-row"
              :class="{ 'is-folder': item.type === 'directory' }"
              @click="handleItemClick(item)"
            >
              <!-- Name Column -->
              <td class="col-name">
                <div class="d-flex align-center ga-3 overflow-hidden">
                  <v-icon
                    :icon="getItemIcon(item)"
                    :color="getItemColor(item)"
                    size="20"
                    class="flex-shrink-0"
                  />
                  <span class="item-name text-truncate" :title="item.name">
                    {{ item.name }}
                  </span>
                </div>
              </td>

              <!-- Size Column -->
              <td class="col-size text-right text-caption text-medium-emphasis">
                {{ item.type === 'directory' ? 'โฟลเดอร์' : formatBytes(item.size) }}
              </td>

              <!-- Modified Date Column -->
              <td class="col-mtime text-right text-caption text-medium-emphasis d-none d-sm-table-cell">
                {{ formatDate(item.mtime) }}
              </td>

              <!-- Action Column -->
              <td class="col-action text-right">
                <v-btn
                  v-if="item.type === 'directory'"
                  icon="mdi-chevron-right"
                  variant="text"
                  density="compact"
                  size="small"
                  color="medium-emphasis"
                />
                <v-btn
                  v-else
                  icon="mdi-play-circle-outline"
                  variant="text"
                  density="compact"
                  size="small"
                  color="primary"
                  title="เปิดดูตัวอย่าง"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Explorer Footer Status Bar -->
    <div class="explorer-footer">
      <div class="d-flex align-center ga-4 text-caption text-medium-emphasis">
        <span>
          <v-icon icon="mdi-folder-outline" size="14" class="mr-1" />
          {{ folderCount }} โฟลเดอร์
        </span>
        <span>
          <v-icon icon="mdi-file-outline" size="14" class="mr-1" />
          {{ fileCount }} ไฟล์
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import {
  fetchSkoDirectory,
  formatBytes,
  getExtension,
  getCompatibleTypes
} from '../../utils/skoUrlResolver.js'

const props = defineProps({
  initialPath: { type: String, default: '' },
  basePrefix: { type: String, default: '' }
})

const emit = defineEmits(['select-file', 'path-change', 'media-list-ready'])

const currentPath = ref(props.initialPath || '')
const items = ref([])
const loading = ref(false)
const error = ref('')
const searchQuery = ref('')

// Breadcrumbs computed
const pathCrumbs = computed(() => {
  if (!currentPath.value) return []
  const segments = currentPath.value.split('/').filter(Boolean)
  let accumulated = ''
  return segments.map(seg => {
    accumulated = accumulated ? `${accumulated}/${seg}` : seg
    return { name: seg, path: accumulated }
  })
})

// Filtered items by search query
const filteredItems = computed(() => {
  if (!searchQuery.value) return items.value
  const q = searchQuery.value.toLowerCase()
  return items.value.filter(item => item.name.toLowerCase().includes(q))
})

const folderCount = computed(() => items.value.filter(i => i.type === 'directory').length)
const fileCount = computed(() => items.value.filter(i => i.type !== 'directory').length)

// Icon resolver for files
function getItemIcon(item) {
  if (item.type === 'directory') return 'mdi-folder'
  const ext = getExtension(item.name)

  if (['mp4', 'webm', 'mkv', 'mov', 'avi', 'm4v', 'ts'].includes(ext)) return 'mdi-video'
  if (['mid', 'midi', 'kar', 'emk', 'lyc', 'lyr', 'cur'].includes(ext)) return 'mdi-piano'
  if (ext === 'stem' || ext === 'stem.mp4') return 'mdi-tune-vertical'
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac', 'opus'].includes(ext)) return 'mdi-music'
  if (['pdf'].includes(ext)) return 'mdi-file-pdf-box'
  if (['docx', 'dotx', 'doc'].includes(ext)) return 'mdi-file-word'
  if (['xlsx', 'xls', 'csv'].includes(ext)) return 'mdi-file-excel'
  if (['pptx', 'ppsx', 'ppt'].includes(ext)) return 'mdi-file-powerpoint'
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg'].includes(ext)) return 'mdi-image'
  return 'mdi-file-document-outline'
}

function getItemColor(item) {
  if (item.type === 'directory') return 'amber-darken-1'
  const ext = getExtension(item.name)

  if (['mp4', 'webm', 'mkv', 'mov', 'avi'].includes(ext)) return 'blue'
  if (['mid', 'midi', 'kar', 'emk', 'lyc', 'lyr', 'cur'].includes(ext)) return 'purple'
  if (ext === 'stem' || ext === 'stem.mp4') return 'cyan'
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a'].includes(ext)) return 'teal'
  if (['pdf'].includes(ext)) return 'red'
  if (['docx', 'doc'].includes(ext)) return 'indigo'
  if (['xlsx', 'xls', 'csv'].includes(ext)) return 'green'
  if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) return 'pink'
  return 'blue-grey'
}

function formatDate(mtime) {
  if (!mtime) return '-'
  try {
    const d = new Date(mtime)
    return d.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return mtime
  }
}

async function loadDirectory(pathStr) {
  loading.value = true
  error.value = ''
  try {
    const res = await fetchSkoDirectory(pathStr)
    items.value = res.items
    currentPath.value = res.currentPath
    emit('path-change', res.currentPath)

    // Collect media items for potential playlist queue
    const mediaFiles = res.items
      .filter(i => i.type !== 'directory')
      .map(i => {
        const fullRel = res.currentPath ? `${res.currentPath}/${i.name}` : i.name
        const comp = getCompatibleTypes(i.name)
        return {
          name: i.name,
          path: fullRel,
          type: comp.defaultType,
          types: comp.types
        }
      })
      .filter(i => ['video', 'audio', 'stem', 'midi'].includes(i.type))

    emit('media-list-ready', mediaFiles)
  } catch (err) {
    error.value = err.message || 'ไม่สามารถติดต่อเซิร์ฟเวอร์ได้'
  } finally {
    loading.value = false
  }
}

function navigateTo(pathStr) {
  loadDirectory(pathStr)
}

function navigateUp() {
  if (!currentPath.value) return
  const parts = currentPath.value.split('/').filter(Boolean)
  parts.pop()
  navigateTo(parts.join('/'))
}

function handleItemClick(item) {
  if (item.type === 'directory') {
    const nextPath = currentPath.value ? `${currentPath.value}/${item.name}` : item.name
    navigateTo(nextPath)
  } else {
    const fullRelPath = currentPath.value ? `${currentPath.value}/${item.name}` : item.name
    const comp = getCompatibleTypes(item.name)
    emit('select-file', {
      name: item.name,
      relativePath: fullRelPath,
      defaultType: comp.defaultType,
      compatibleTypes: comp.types,
      siblingItems: items.value
    })
  }
}

watch(() => props.initialPath, (newPath) => {
  if (newPath !== currentPath.value) {
    loadDirectory(newPath || '')
  }
})

onMounted(() => {
  loadDirectory(currentPath.value)
})
</script>

<style scoped>
.folder-explorer-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: var(--bg-base, #090d16);
  color: var(--text-main, #f1f5f9);
  overflow: hidden;
}

.explorer-header {
  padding: 10px 16px;
  background: var(--bg-surface, #0f172a);
  border-bottom: 1px solid var(--border-color, #2b3145);
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.breadcrumbs-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.04);
  padding: 2px 8px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.breadcrumb-item {
  font-size: 13px;
  letter-spacing: 0.2px;
}

.search-box {
  width: 240px;
}

.explorer-body {
  flex: 1;
  overflow-y: auto;
  position: relative;
}

.directory-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}

.directory-table th {
  position: sticky;
  top: 0;
  background: var(--bg-card, #171f33);
  padding: 10px 16px;
  font-weight: 600;
  color: #94a3b8;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--border-color, #2b3145);
  z-index: 10;
}

.directory-table td {
  padding: 10px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.item-row {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.item-row:hover {
  background-color: rgba(59, 130, 246, 0.08);
}

.item-name {
  font-weight: 500;
  color: var(--text-main, #f1f5f9);
}

.explorer-footer {
  padding: 8px 16px;
  background: var(--bg-surface, #0f172a);
  border-top: 1px solid var(--border-color, #2b3145);
}
</style>
